/**
 * ProviderDetailsServices Component
 * Feature: 009-provider-ui-pixel-perfect
 * Tasks: T110-T111, T115 - Services tab with comprehensive catalog
 * 
 * Displays provider's services catalog with descriptions, pricing, and price ranges.
 */

import { Briefcase } from 'lucide-react';
import type { ProviderProfile } from '../types/provider-profile.types';
import { ProviderServiceCard } from './ProviderServiceCard';

type ProviderDetailsServicesProps = {
  provider: ProviderProfile;
};

export const ProviderDetailsServices = ({
  provider,
}: ProviderDetailsServicesProps) => {
  const { services } = provider;

  // Services are already sorted by displayOrder from the API
  // Maintain the array order as received
  const sortedServices = services;

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#026857]/10 rounded-lg flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-[#026857]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Services Catalog</h2>
            <p className="text-sm text-gray-600 mt-1">
              Professional services offered by this provider
            </p>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      {sortedServices.length > 0 ? (
        <div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          data-testid="services-grid"
        >
          {sortedServices.map((service) => (
            <ProviderServiceCard key={service.id} service={service} />
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white rounded-lg border border-gray-200 p-12">
          <div className="text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No services listed
            </h3>
            <p className="text-gray-600">
              Contact the provider directly for information about available services.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
